import type { MarketingAttribution } from '../tracking/attribution';
import { isViewContentPath } from '../tracking/config';

/**
 * When the quick WhatsApp lead prompt opens on askflow.sa. Kept free of DOM
 * access so the rules are unit-tested; the component only wires timers.
 *
 * - `paid`: the visit arrived from an ad (click id or paid utm_medium). The
 *   prompt opens a few seconds after landing on any marketing page.
 * - `content`: an organic reader of the homepage or a guide, comparison or
 *   industry page. The prompt waits until they have read half the page.
 */
export type LeadPromptTrigger = 'paid' | 'content';

export const BUSINESS_TYPES = ['courts', 'gym', 'studio', 'academy', 'club', 'clinic', 'other'] as const;

export type BusinessType = (typeof BUSINESS_TYPES)[number];

export type LeadPromptOffer = 'padel' | 'studio' | 'gym' | 'default';

const PAID_MEDIUMS = new Set(['cpc', 'ppc', 'paid', 'paidsearch', 'paid_search', 'paidsocial', 'paid_social', 'display', 'cpm', 'ads']);

const NO_PROMPT_PATHS = ['/privacy-policy', '/terms-of-service', '/cookie-policy'];

export const PAID_PROMPT_DELAY_MS = 5_000;
/** Without a consent choice the prompt still opens, hiding the banner meanwhile. */
export const CONSENT_WAIT_MS = 20_000;
export const CONTENT_SCROLL_DEPTH = 0.5;

const DISMISS_SUPPRESS_MS = 7 * 24 * 60 * 60 * 1000;

function withoutLocale(pathname: string): string {
  return pathname.replace(/^\/ar(?=\/|$)/, '') || '/';
}

export function isPaidVisit(attribution: MarketingAttribution | null): boolean {
  if (!attribution) return false;
  if (attribution.gclid || attribution.gbraid || attribution.wbraid) return true;
  if (attribution.fbclid || attribution.li_fat_id) return true;

  return PAID_MEDIUMS.has((attribution.utm_medium ?? '').toLowerCase());
}

export function leadPromptTrigger(pathname: string, paidSession: boolean): LeadPromptTrigger | null {
  const path = withoutLocale(pathname);

  if (NO_PROMPT_PATHS.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) return null;
  if (paidSession) return 'paid';

  return path === '/' || isViewContentPath(path) ? 'content' : null;
}

export function leadPromptOffer(pathname: string): LeadPromptOffer {
  const path = withoutLocale(pathname);

  if (path.includes('padel')) return 'padel';
  if (path.includes('pilates') || path.includes('studio')) return 'studio';
  if (path.includes('gym')) return 'gym';

  return 'default';
}

export function defaultBusinessType(pathname: string): BusinessType | undefined {
  const path = withoutLocale(pathname);

  if (path.includes('padel')) return 'courts';
  if (path.includes('pilates') || path.includes('studio')) return 'studio';
  if (path.includes('gym')) return 'gym';
  if (path.includes('academy')) return 'academy';
  if (path.includes('clinic')) return 'clinic';
  if (path.includes('membership-club')) return 'club';

  return undefined;
}

export interface LeadPromptMemory {
  state: 'dismissed' | 'submitted';
  at: number;
}

export function parseLeadPromptMemory(value: string | null): LeadPromptMemory | null {
  if (!value) return null;

  try {
    const parsed: unknown = JSON.parse(value);

    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'state' in parsed &&
      'at' in parsed &&
      (parsed.state === 'dismissed' || parsed.state === 'submitted') &&
      typeof parsed.at === 'number'
    ) {
      return { state: parsed.state, at: parsed.at };
    }
  } catch {
    // A corrupt value is treated as no memory.
  }

  return null;
}

/** A submitted lead is never prompted again; a dismissal rests for a week. */
export function isLeadPromptSuppressed(memory: LeadPromptMemory | null, now: number): boolean {
  if (!memory) return false;
  if (memory.state === 'submitted') return true;

  return now - memory.at < DISMISS_SUPPRESS_MS;
}
